import React, { useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SectionHeading from '../components/SectionHeading';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { CONTACT_EMAIL } from '../data/content';
import { Mail, Send, Clock, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', plan: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.name.trim().length < 2) return toast({ title: 'Please enter your name', variant: 'destructive' });
    if (!EMAIL_RE.test(form.email)) return toast({ title: 'Invalid email', description: 'Please enter a valid email address.', variant: 'destructive' });
    if (!form.message || form.message.trim().length < 10) return toast({ title: 'Message too short', description: 'Please write at least 10 characters.', variant: 'destructive' });
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name: form.name.trim(), email: form.email.trim(), subject: form.subject.trim(), plan: form.plan, message: form.message.trim() });
      toast({ title: 'Message sent', description: `Thanks ${form.name.split(' ')[0]} — we'll reply within 12 hours.` });
      setForm({ name: '', email: '', subject: '', plan: '', message: '' });
      setSent(true);
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Something went wrong. Please try again in a few minutes.', variant: 'destructive' });
    } finally { setSending(false); }
  };

  return (
    <>
      <Header />
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Contact" title="Talk to a person, not a bot." lead="Questions about the Daily Double, a plan or a payment? Write to us. A member of the team replies within 12 hours — usually much sooner." />
            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-ice flex items-center justify-center"><Mail className="w-5 h-5 text-royal" /></div>
                <div><div className="eyebrow text-slate-500">Email</div><a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-navy hover:text-royal break-all" data-testid="contact-email">{CONTACT_EMAIL}</a></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-ice flex items-center justify-center"><Clock className="w-5 h-5 text-royal" /></div>
                <div><div className="eyebrow text-slate-500">Response time</div><div className="font-semibold text-navy">Within 12 hours, every day</div></div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <div className="bg-white border border-win/30 rounded-3xl p-8 md:p-10 text-center" data-testid="contact-success">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-win/10 text-win flex items-center justify-center"><CheckCircle2 className="w-7 h-7" /></div>
                <h2 className="mt-5 font-display text-2xl font-extrabold text-navy">Message delivered</h2>
                <p className="mt-2 text-slate-600">Your message is in our inbox. We will reply within 12 hours.</p>
                <Button type="button" onClick={() => setSent(false)} className="mt-6 bg-royal hover:bg-royal-dark text-white h-11 font-semibold rounded-full px-6">Send another message</Button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="bg-white border border-line rounded-3xl p-6 md:p-8" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label htmlFor="c-name">Name *</Label><Input id="c-name" data-testid="contact-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-12 rounded-xl" /></div>
                  <div><Label htmlFor="c-email">Email *</Label><Input id="c-email" data-testid="contact-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-12 rounded-xl" /></div>
                  <div><Label htmlFor="c-sub">Subject</Label><Input id="c-sub" data-testid="contact-subject" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className="mt-1.5 h-12 rounded-xl" /></div>
                  <div>
                    <Label htmlFor="c-plan">Topic</Label>
                    <Select value={form.plan} onValueChange={v => setForm({ ...form, plan: v })}>
                      <SelectTrigger id="c-plan" data-testid="contact-topic" className="mt-1.5 h-12 rounded-xl"><SelectValue placeholder="Select a topic (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Turkish Team">Turkish Team (Daily Double)</SelectItem>
                        <SelectItem value="Asian Tips">Asian Tips (Asian Handicap)</SelectItem>
                        <SelectItem value="24 BOX Tips">24 BOX Tips (Daily Single)</SelectItem>
                        <SelectItem value="Payment question">Payment question</SelectItem>
                        <SelectItem value="General">General enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2"><Label htmlFor="c-msg">Message *</Label><Textarea id="c-msg" data-testid="contact-message" rows={6} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                </div>
                <Button type="submit" size="lg" disabled={sending} data-testid="contact-submit" className="mt-6 w-full sm:w-auto bg-royal hover:bg-royal-dark text-white h-12 px-8 font-semibold rounded-full disabled:opacity-70">
                  {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-2" /> Send message</>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
